import { useQuery, type UseQueryOptions, type UseQueryResult } from 'react-query';

export interface EntityQueryConfig<TData, TParams> {
  queryKey: string[];
  queryFn: (params: TParams | undefined) => Promise<TData>;
  options?: Omit<UseQueryOptions<TData, unknown, TData, unknown[]>, 'queryKey' | 'queryFn'>;
}

/**
 * Factory that creates a typed useQuery wrapper.
 * Returns a hook function: (params?, overrides?) => UseQueryResult<TData>
 *
 * Uses react-query v3 positional args internally.
 */
export function createEntityQuery<TData, TParams = void>(
  config: EntityQueryConfig<TData, TParams>
) {
  return function useEntityQuery(
    params?: TParams,
    overrides?: Omit<UseQueryOptions<TData, unknown, any, unknown[]>, 'queryKey' | 'queryFn'>
  ): UseQueryResult<any> {
    const fullKey: unknown[] =
      params !== undefined ? [...config.queryKey, params] : config.queryKey;

    return useQuery(
      fullKey,
      async () => config.queryFn(params),
      {
        ...config.options,
        ...overrides,
      }
    );
  };
}
